import { useEffect, useState } from 'react';

import { socket } from './socket';

/**
 * Instalación como aplicación (PWA).
 *
 * El service worker solo se registra en producción: en desarrollo guardaría
 * en caché los módulos de Vite y la recarga en caliente dejaría de funcionar.
 */

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((listener) => listener());

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // Una reconexión del WebSocket suele significar que el servidor se
        // ha reiniciado con una versión nueva: se comprueba al momento en
        // vez de esperar a que el navegador lo haga por su cuenta.
        let connectedBefore = false;
        socket.on('status', (status) => {
          if (status !== 'open') return;
          if (connectedBefore) void registration.update().catch(() => undefined);
          connectedBefore = true;
        });
      })
      .catch((error) => {
        console.warn('No se pudo registrar el service worker', error);
      });
  });

  // Si entra una versión nueva, la pestaña se recarga para no seguir con el
  // código viejo, salvo en mitad de una partida, donde sería un estorbo.
  const hadController = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) return;
    if (location.pathname.startsWith('/partida/')) return;
    location.reload();
  });

  // El navegador ofrece instalar: se guarda el aviso para lanzarlo desde un
  // botón nuestro, en vez de la barra genérica.
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    changed();
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    changed();
  });
}

/** `install` es null si el navegador no permite instalar ahora mismo. */
export function useInstallPrompt(): { install: (() => Promise<void>) | null } {
  const [, force] = useState(0);

  useEffect(() => {
    const listener = () => force((n) => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  if (!deferred) return { install: null };
  return {
    install: async () => {
      const event = deferred;
      if (!event) return;
      await event.prompt();
      await event.userChoice;
      deferred = null;
      changed();
    },
  };
}
